import {
  useRouteError,
  isRouteErrorResponse,
  useNavigate,
} from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLogout } from "@/hooks/auth/useLogout";
import { HomeIcon, RefreshCcw, ArrowLeft, LogOut } from "lucide-react";
import { motion } from "framer-motion";

const ErrorPage = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  let errorMessage: string;
  let statusCode: number;

  if (isRouteErrorResponse(error)) {
    errorMessage = error.data?.message || error.statusText;
    statusCode = error.status;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    statusCode = 500;
  } else if (typeof error === "string") {
    errorMessage = error;
    statusCode = 500;
  } else {
    errorMessage = "Une erreur inconnue est survenue";
    statusCode = 500;
  }

  // The normal logout: ends the API session and clears the user's state
  // instead of wiping all of localStorage (SFRESTO-164). A full page load then
  // leaves the broken screen behind.
  const logout = useLogout();
  const handleLogoutAndReset = async () => {
    await logout();
    window.location.assign("/auth/login");
  };

  const getErrorTitle = (status: number) => {
    switch (status) {
      case 404:
        return "Page introuvable";
      case 401:
        return "Non autorisé";
      case 403:
        return "Accès refusé";
      case 500:
        return "Erreur serveur";
      default:
        return "Une erreur est survenue";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="text-center max-w-3xl mx-auto">
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-9xl font-bold text-primary mb-2"
        >
          {statusCode}
        </motion.h1>

        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-2xl font-semibold text-th-gray-22 mb-4"
        >
          {getErrorTitle(statusCode)}
        </motion.h2>

        {errorMessage && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-gray-600 mb-20"
          >
            {errorMessage}
          </motion.p>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-3 justify-center"
        >
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="flex items-center gap-2 bg-white hover:bg-gray-100"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            className="flex items-center gap-2 bg-white hover:bg-gray-100"
          >
            <RefreshCcw className="w-4 h-4" />
            Réessayer
          </Button>
          <Button
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
          >
            <HomeIcon className="w-4 h-4" />
            Accueil
          </Button>
          <Button
            onClick={handleLogoutAndReset}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Se déconnecter
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default ErrorPage;
